// Model hlavního menu — JEDINÉ místo, kde se čte nativní `#navigation`.
//
// Nativní menu Jiposu není ploché, nese celé 3 úrovně:
//   ul.menu-level-1 > li > a>b
//                        > ul.menu-level-2 > li[.has-third-level] > a.menu-image
//                                                                 > div > a>span
//                                                                       > ul.menu-level-3 > li > a
// (výjimka: `li#nav-manufacturers` = Značky — úroveň 2 je `li > a > span`, bez
// obrázku i bez 3. úrovně.)
//
// Konzumenti: megaMenu.js (desktop) a mobileNav.js (drawer). Model vrací i
// reference na uzly, aby si mobilní drawer mohl nativní strom rovnou dekorovat
// místo stavění kopie (Shoptet `class`/`data-testid` musí zůstat na místě).

import { byPage, byRoot } from './pages.js';
import { isSalePath, saleLabel } from './campaign.js';
import L from '../locale/cs/menuModel.js';

// Ikony root kategorií — JEDNA sada pro celý web (drawer, desktop lišta, mega
// menu, mobilní search sheet, našeptávač, výsledky hledání). Od 2026-08-31 sada
// z handoveru mobilního menu (design_handoff_mobilni_menu/README.md §Ikony
// hlavních kategorií, Lucide-like outline); sjednocení na desktop rozhodl Tomáš
// týž den. Drawer má fallback 'tag' (handover), ostatní konzumenti drží 'grid'.
export const CAT_ICONS = {
  ...byPage({ sale: 'percent', clearance: 'tag', brands: 'rosette' }),
  // kořeny katalogu pod sémantickým klíčem (cesty per shop = `catalog.roots`)
  ...byRoot({
    garden: 'sprout',
    build: 'bricks',
    workshop: 'wrench',
    home: 'armchair',
    kids: 'swing',
    car: 'car',
    xmas: 'tree',
    ppe: 'helmet',
    solar: 'solar',
  }),
};

// Ikony skupin podkategorií (mega menu; sada přegenerovaná 2026-08-31 na
// zadání Tomáše — každé skupině relevantní glyph ve stylu root sady, symboly
// v sprite.svg). Katalog má přes 90 skupin a admin je mění, takže se neudržuje
// výčet, ale pravidla podle názvu — PRVNÍ shoda vyhrává, od konkrétních
// k obecným (pořadí je nosné: „Foliovníky" musí předběhnout /foli/ → layers,
// „Ochrana proti ptákům" generický /ochran/ → shield). Co se netrefí, DĚDÍ
// ikonu své root kategorie (vědomě: „Ostatní příslušenství…", „Vibrační
// desky", značky).
const GROUP_ICON_RULES = L.groupIconRules;

export function groupIcon(label, fallback) {
  for (const [re, name] of GROUP_ICON_RULES) {
    if (re.test(label)) return name;
  }
  return fallback || 'grid';
}

export function pathOf(href) {
  if (!href) return '';
  try {
    return new URL(href, window.location.origin).pathname;
  } catch (e) {
    return href;
  }
}

export function textOf(node) {
  return node ? node.textContent.replace(/\s+/g, ' ').replace(/,\s*$/, '').trim() : '';
}

// Odkaz na kategorii uvnitř `li` úrovně 2 (obrázkový odkaz je jen dekorace).
function level2Link(li) {
  return li.querySelector(':scope > div > a') || li.querySelector(':scope > a:not(.menu-image)');
}

export function readMenu() {
  const items = document.querySelectorAll('#navigation ul.menu-level-1 > li');
  const roots = [];
  // Po dobu kampaně se akční nabídka jmenuje podle deštníku („Říjnový
  // výprodej", campaign.js) v liště, panelu i draweru; admin drží „Slevoakce".
  const campaignLabel = saleLabel();
  for (const li of items) {
    const a = li.querySelector(':scope > a');
    if (!a) continue;
    const href = a.getAttribute('href');
    if (!href) continue;
    const label = (campaignLabel && isSalePath(pathOf(href)) && campaignLabel) || textOf(a.querySelector('b') || a);
    if (!label) continue;

    const groups = [];
    for (const li2 of li.querySelectorAll(':scope > ul.menu-level-2 > li')) {
      const link = level2Link(li2);
      if (!link) continue;
      const links = [];
      for (const a3 of li2.querySelectorAll(':scope > div > ul.menu-level-3 > li > a')) {
        links.push({ href: a3.getAttribute('href'), label: textOf(a3), el: a3 });
      }
      groups.push({
        el: li2,
        link,
        image: li2.querySelector(':scope > a.menu-image'),
        sub: li2.querySelector(':scope > div > ul.menu-level-3'),
        href: link.getAttribute('href'),
        label: textOf(link),
        links,
      });
    }
    roots.push({
      el: li,
      link: a,
      sub: li.querySelector(':scope > ul.menu-level-2'),
      href: href,
      path: pathOf(href),
      label,
      groups,
    });
  }
  return roots;
}
